import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Package, Bug } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const WhatsNew = () => {
  const navigate = useNavigate();
  const [changelogs, setChangelogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchChangelogs();
  }, []);

  const fetchChangelogs = async () => {
    try {
      const response = await axios.get(`${API}/changelog/list`);
      setChangelogs(response.data.changelogs);
    } catch (error) {
      toast.error('Failed to load changelog');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
      <header className="glass sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              onClick={() => navigate(-1)}
              variant="ghost"
              className="text-white hover:bg-white/20"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back
            </Button>
            <h1 className="text-2xl font-bold text-white" style={{ fontFamily: 'Space Grotesk' }}>
              What's New
            </h1>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-12 space-y-8">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-white mb-4" style={{ fontFamily: 'Space Grotesk' }}>
            Product Updates & Changelog
          </h2>
          <p className="text-white/80">
            Stay up to date with the latest features and improvements
          </p>
        </div>

        {loading ? (
          <div className="text-white text-center py-12">Loading changelog...</div>
        ) : changelogs.length === 0 ? (
          <Card className="glass border-white/20">
            <CardContent className="py-12 text-center">
              <p className="text-white/70">No updates available yet</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {changelogs.map((changelog) => (
              <Card key={changelog.id} className="glass border-white/20">
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="text-2xl text-white" style={{ fontFamily: 'Space Grotesk' }}>
                        {changelog.title}
                      </CardTitle>
                      <CardDescription className="text-white/70 mt-2">
                        Version {changelog.version} • {formatDate(changelog.release_date)}
                      </CardDescription>
                    </div>
                    <div className="bg-white/20 px-3 py-1 rounded-full text-white text-sm">
                      v{changelog.version}
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-6">
                  <p className="text-white/90">{changelog.description}</p>

                  {changelog.features && changelog.features.length > 0 && (
                    <div>
                      <div className="flex items-center gap-2 mb-3">
                        <Package className="w-5 h-5 text-green-400" />
                        <h3 className="text-lg font-semibold text-white">New Features</h3>
                      </div>
                      <ul className="space-y-2">
                        {changelog.features.map((feature, idx) => (
                          <li key={idx} className="text-white/80 flex items-start gap-2">
                            <span className="text-green-400 mt-1">✓</span>
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {changelog.bug_fixes && changelog.bug_fixes.length > 0 && (
                    <div>
                      <div className="flex items-center gap-2 mb-3">
                        <Bug className="w-5 h-5 text-blue-400" />
                        <h3 className="text-lg font-semibold text-white">Bug Fixes</h3>
                      </div>
                      <ul className="space-y-2">
                        {changelog.bug_fixes.map((fix, idx) => (
                          <li key={idx} className="text-white/80 flex items-start gap-2">
                            <span className="text-blue-400 mt-1">•</span>
                            <span>{fix}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default WhatsNew;
